
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";

// Types
import {
  ICategoryProviderData,
  ICategoryProviderMethods
} from "@/models/providers/categories";
import { IState } from "@/store";
import { IProductCategory } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      rootCategoriesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
        ](this.brandId, this.productTypes);
      },
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CATALOGUE_CATEGORIES,
          scope: this.rootCategoriesScope
        });
      },
      categoryScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE_CATEGORIES}/scope`](
          this.categoryId
        );
      },
      category(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE_CATEGORIES,
          scope: this.categoryScope
        });
      }
    })
  }
})
export default class CategoryProvider extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Map State

  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public category!: IProductCategory;
  public categoryScope!: string;

  // Props

  @Prop({ required: true })
  public categoryId!: IProductCategory["id"];

  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: this.categoryScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getRootCategories(this.isReloading),
        this.getCategory(this.isReloading)
      ]);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getCategory(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/get`,
      {
        id: this.categoryId,
        productTypes: this.productTypes,
        brandId: this.brandId,
        scope: this.categoryScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public getRootCategories(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/list`,
      {
        productTypes: this.productTypes,
        scope: this.rootCategoriesScope,
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public deleteCategory() {
    if (!this.canDelete) return;

    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/deleteCategory`,
      {
        id: this.categoryId,
        productTypes: this.productTypes,
        onSuccess: () => {
          this.$router.push({
            name: this.isOption
              ? "adminCatalogueOptions"
              : this.isAttribute
              ? "adminCatalogueAttributes"
              : "adminCatalogueProducts"
          });
        }
      }
    );
  }

  public addSubcategory: ICategoryProviderMethods["addSubcategory"] = ({
    redirect = false
  }) => {
    if (!this.canCreate) return;
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/openAddEditCategoryModal`,
      {
        productTypes: this.productTypes,
        parentCategoryId: this.categoryId,
        redirect
      }
    );
  };

  public addProduct() {
    if (!this.canCreateProduct) return;

    this.$store.dispatch(`data/${DataModules.CATALOGUE}/openAddProductModal`, {
      props: {
        productTypes: this.productTypes,
        categoryId: this.categoryId
      }
    });
  }

  public updateCategory(data) {
    if (!this.canUpdate) return;

    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/update`,
      {
        id: this.categoryId,
        productTypes: this.productTypes,
        data
      }
    );
  }

  // Computed

  public get canCreate() {
    if (this.isStaged) return false;
    if (this.isOption)
      return this.$userCan("create_product_attributes_category");
    if (this.isAttribute)
      return this.$userCan("create_product_options_category");
    return this.$userCan("create_product_category");
  }

  public get canUpdate() {
    if (this.isStaged) return false;
    if (this.isOption)
      return this.$userCan("update_product_attributes_category");
    if (this.isAttribute)
      return this.$userCan("update_product_options_category");
    return this.$userCan("update_product_category");
  }

  public get canDelete() {
    if (this.isStaged) return false;
    if (this.isOption)
      return this.$userCan("delete_product_attributes_category");
    if (this.isAttribute)
      return this.$userCan("delete_product_options_category");
    return this.$userCan("delete_product_category");
  }

  public get canCreateProduct() {
    if (this.isStaged) return false;

    return this.$userCan("create_product");
  }

  public get isStaged() {
    return !!this.$_.get(this.category, "staged_import");
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  // Guarding

  public get guardFunctionalities() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? ["get_product_options_category", "list_product_options_categories"]
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? [
          "get_product_attributes_category",
          "list_product_attributes_categories"
        ]
      : ["get_product_category", "list_product_categories"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("categoryProviderData")
  public categoryProviderData(): ICategoryProviderData {
    return {
      canCreate: this.canCreate,
      canCreateProduct: this.canCreateProduct,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      category: this.category,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isStaged: this.isStaged,
      rootCategories: this.rootCategories
    };
  }

  @Provide("categoryProviderMethods")
  public get categoryProviderMethods(): ICategoryProviderMethods {
    return {
      addProduct: this.addProduct,
      addSubcategory: this.addSubcategory,
      deleteCategory: this.deleteCategory,
      reloadData: this.reloadData,
      updateCategory: this.updateCategory
    };
  }

  // Render function
  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $categoryData: this.categoryProviderData(),
        $categoryMethods: this.categoryProviderMethods
      })
    );
  }
}
